import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Mail, Rocket, Target } from 'lucide-react';
import {
    FaWordpress,
    FaFacebookF,
    FaTwitter,
    FaLinkedinIn,
    FaInstagram,
    FaYoutube,
    FaEnvelope,
    FaPhone,
    FaMapMarkerAlt,
    FaArrowRight,
    FaHeart,
    FaCertificate,
    FaShieldAlt
} from 'react-icons/fa';
import { useLanguage } from '../i18n';

const Footer = () => {
    const { language, t } = useLanguage();
    const [email, setEmail] = useState('');
    const [isSubscribed, setIsSubscribed] = useState(false);

    const handleNewsletterSubmit = (e) => {
        e.preventDefault();
        if (email) {
            // Simulate subscription
            setIsSubscribed(true);
            setEmail('');
            setTimeout(() => setIsSubscribed(false), 3000);
        }
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const footerLinks = {
        services: [
            { name: language === 'vi' ? 'Theme WordPress Tùy chỉnh' : 'Custom WordPress themes', action: () => scrollToSection('services') },
            { name: language === 'vi' ? 'Phát triển Plugin' : 'Plugin development', action: () => scrollToSection('services') },
            { name: 'WooCommerce', action: () => scrollToSection('services') },
            { name: language === 'vi' ? 'Tối ưu hóa SEO' : 'SEO optimization', action: () => scrollToSection('services') },
            { name: language === 'vi' ? 'Bảo trì Website' : 'Website maintenance', action: () => scrollToSection('services') },
            { name: language === 'vi' ? 'Dịch vụ Di chuyển' : 'Migration services', action: () => scrollToSection('services') }
        ],
        company: [
            { name: t('about'), to: '/about', type: 'link' },
            { name: t('projects'), action: () => scrollToSection('case-studies'), type: 'button' },
            { name: t('pricing'), action: () => scrollToSection('pricing'), type: 'button' },
            { name: 'Blog', action: () => scrollToSection('blog'), type: 'button' },
            { name: 'FAQ', action: () => scrollToSection('faq'), type: 'button' },
            { name: t('contact'), action: () => scrollToSection('contact'), type: 'button' }
        ],
        resources: [
            { name: 'Mẹo WordPress', href: '#' },
            { name: 'Hướng dẫn SEO', href: '#' },
            { name: 'Template Thiết kế', href: '#' },
            { name: 'Đánh giá Plugin', href: '#' },
            { name: 'Hướng dẫn Bảo mật', href: '#' },
            { name: 'Mẹo Hiệu suất', href: '#' }
        ],
        legal: [
            { name: t('privacy'), to: '/privacy-policy', type: 'link' },
            { name: t('terms'), to: '/terms-of-service', type: 'link' },
            { name: 'Chính sách Cookie', href: '#', type: 'external' },
            { name: 'Chính sách Hoàn tiền', href: '#', type: 'external' },
            { name: 'Thỏa thuận Giấy phép', href: '#', type: 'external' }
        ]
    };

    const socialLinks = [
        { icon: <FaFacebookF />, href: 'https://facebook.com/wordpressdevservices', name: 'Facebook' },
        { icon: <FaTwitter />, href: 'https://twitter.com/wpdevservices', name: 'Twitter' },
        { icon: <FaLinkedinIn />, href: 'https://www.linkedin.com/company/devenflyweb', name: 'LinkedIn' },
        { icon: <FaInstagram />, href: 'https://instagram.com/wpdevservices', name: 'Instagram' },
        { icon: <FaYoutube />, href: 'https://youtube.com/wpdevservices', name: 'YouTube' }
    ];

    const certifications = [
        { name: 'WordPress Studio', icon: <FaWordpress className="text-primary-400" /> },
        { name: 'Bảo mật SSL', icon: <FaShieldAlt className="text-green-600" /> },
        { name: 'Chứng nhận ISO', icon: <FaCertificate className="text-yellow-600" /> }
    ];

    return (
        <footer className="bg-gray-900 text-white">
            {/* Main Footer Content */}
            <div className="container mx-auto px-4 py-16">
                <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
                    {/* Company Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-1"
                    >
                        {/* Logo */}
                        <div className="mb-6">
                            <div className="inline-flex rounded-lg bg-white px-3 py-2">
                                <img src="/devenfly-wordmark.png" alt="DevenflyWeb" className="h-auto w-44 object-contain" />
                            </div>
                            <p className="mt-3 text-gray-400 text-sm">{language === 'vi' ? 'Dịch vụ phát triển' : 'Development services'}</p>
                        </div>

                        <p className="text-gray-300 mb-6 leading-relaxed">
                            {language === 'vi' ? 'Dịch vụ phát triển WordPress chuyên nghiệp giúp doanh nghiệp tạo ra website nhanh, bảo mật và có thể mở rộng. Chuyển đổi sự hiện diện trực tuyến của bạn ngay hôm nay.' : 'Professional WordPress development helps businesses create websites that are fast, secure, and ready to scale. Transform your online presence today.'}
                        </p>

                        {/* Contact Info */}
                        <div className="space-y-3 mb-6">
                            <div className="flex items-center space-x-3 text-gray-300">
                                <FaPhone className="text-primary-400" />
                                <span>+84 (0) 123 456 789</span>
                            </div>
                            <div className="flex items-center space-x-3 text-gray-300">
                                <FaEnvelope className="text-primary-400" />
                                <span>hongducmmo@gmail.com</span>
                            </div>
                            <div className="flex items-center space-x-3 text-gray-300">
                                <FaMapMarkerAlt className="text-primary-400" />
                                <span>318/47 Phan Văn Trị, Phường 11, Bình Thạnh, TP.HCM</span>
                            </div>
                        </div>

                        {/* Social Links */}
                        <div className="flex space-x-4">
                            {socialLinks.map((social, index) => (
                                <motion.a
                                    key={index}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 bg-gray-800 hover:bg-primary-600 rounded-full flex items-center justify-center transition-colors"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    aria-label={social.name}
                                >
                                    {social.icon}
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>

                    {/* Services Links */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <h3 className="text-xl font-bold mb-6">{t('services')}</h3>
                        <ul className="space-y-3">
                            {footerLinks.services.map((link, index) => (
                                <li key={index}>
                                    <button
                                        onClick={link.action}
                                        className="text-gray-300 hover:text-primary-400 transition-colors cursor-pointer"
                                    >
                                        {link.name}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </motion.div>

                    {/* Company Links */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <h3 className="text-xl font-bold mb-6">{language === 'vi' ? 'Công ty' : 'Company'}</h3>
                        <ul className="space-y-3 mb-8">
                            {footerLinks.company.map((link, index) => (
                                <li key={index}>
                                    {link.type === 'link' ? (
                                        <Link
                                            to={link.to}
                                            className="text-gray-300 hover:text-primary-400 transition-colors"
                                        >
                                            {link.name}
                                        </Link>
                                    ) : (
                                        <button
                                            onClick={link.action}
                                            className="text-gray-300 hover:text-primary-400 transition-colors cursor-pointer"
                                        >
                                            {link.name}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* Certifications */}
                        <div>
                            <h4 className="font-semibold mb-3">{language === 'vi' ? 'Chứng nhận' : 'Certifications'}</h4>
                            <div className="space-y-2">
                                {certifications.map((cert, index) => (
                                    <div key={index} className="flex items-center space-x-2 text-sm text-gray-300">
                                        {cert.icon}
                                        <span>{cert.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>

                    {/* Newsletter */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                    >
                        <h3 className="text-xl font-bold mb-6">{t('newsletter')}</h3>
                        <p className="text-gray-300 mb-6">
                            {language === 'vi' ? 'Nhận mẹo WordPress mới nhất, tin tức ngành và ưu đãi độc quyền được gửi đến hộp thư của bạn.' : 'Get the latest WordPress tips, industry news, and exclusive offers delivered to your inbox.'}
                        </p>

                        {/* Newsletter Form */}
                        <form onSubmit={handleNewsletterSubmit} className="mb-6">
                            <div className="flex flex-col space-y-3">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={language === 'vi' ? 'Nhập email của bạn' : 'Enter your email'}
                                    className="px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-colors"
                                    required
                                />
                                <motion.button
                                    type="submit"
                                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center group"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {isSubscribed ? (
                                        <span className="flex items-center gap-2"><CheckCircle2 size={18} aria-hidden="true" /> {t('subscribed')}</span>
                                    ) : (
                                        <>
                                            {t('subscribe')}
                                            <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )}
                                </motion.button>
                            </div>
                        </form>

                        <div className="text-sm text-gray-400">
                            <p className="mb-2 flex items-center gap-2"><Mail size={16} aria-hidden="true" /> {language === 'vi' ? 'Mẹo WordPress hàng tuần' : 'Weekly WordPress tips'}</p>
                            <p className="mb-2 flex items-center gap-2"><Rocket size={16} aria-hidden="true" /> {language === 'vi' ? 'Tài nguyên độc quyền' : 'Exclusive resources'}</p>
                            <p className="flex items-center gap-2"><Target size={16} aria-hidden="true" /> {language === 'vi' ? 'Không spam, hủy đăng ký bất cứ lúc nào' : 'No spam, unsubscribe anytime'}</p>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Resources Section */}
            <div className="border-t border-gray-800">
                <div className="container mx-auto px-4 py-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="grid md:grid-cols-2 gap-8"
                    >
                        <div>
                            <h3 className="text-lg font-bold mb-4">{language === 'vi' ? 'Tài nguyên' : 'Resources'}</h3>
                            <div className="grid grid-cols-2 gap-2">
                                {footerLinks.resources.map((link, index) => (
                                    <a
                                        key={index}
                                        href={link.href}
                                        className="text-gray-300 hover:text-primary-400 transition-colors text-sm"
                                    >
                                        {link.name}
                                    </a>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h3 className="text-lg font-bold mb-4">{language === 'vi' ? 'Pháp lý' : 'Legal'}</h3>
                            <div className="grid grid-cols-2 gap-2">
                                {footerLinks.legal.map((link, index) => (
                                    link.type === 'link' ? (
                                        <Link
                                            key={index}
                                            to={link.to}
                                            className="text-gray-300 hover:text-primary-400 transition-colors text-sm"
                                        >
                                            {link.name}
                                        </Link>
                                    ) : (
                                        <a
                                            key={index}
                                            href={link.href}
                                            className="text-gray-300 hover:text-primary-400 transition-colors text-sm"
                                        >
                                            {link.name}
                                        </a>
                                    )
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-800 bg-gray-950">
                <div className="container mx-auto px-4 py-6">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="flex flex-col md:flex-row items-center justify-between text-sm text-gray-400"
                    >
                        <div className="flex items-center space-x-2 mb-4 md:mb-0">
                                <span>© 2025 DevenflyWeb WordPress Studio.</span>
                            <span className="hidden md:inline">|</span>
                            <span className="flex items-center">
                                {language === 'vi' ? 'Bởi' : 'By'} <FaHeart className="text-red-500 mx-1" /> Duc Nguyen {language === 'vi' ? 'cho những website tuyệt vời' : 'for great websites'}
                            </span>
                        </div>

                        <div className="flex items-center space-x-6 text-xs">
                            <span>{language === 'vi' ? 'Chuyên gia WordPress từ 2020' : 'WordPress experts since 2020'}</span>
                            <span>•</span>
                            <span>{language === 'vi' ? 'Hơn 500 Website' : '500+ websites'}</span>
                            <span>•</span>
                            <span>{language === 'vi' ? '98% Khách hàng Hài lòng' : '98% client satisfaction'}</span>
                        </div>
                    </motion.div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
